/**
 * @file ForbiddenPage.tsx
 * @brief 403 page shown when an authenticated user lacks panel access
 *        (e.g. plain `role === 'user'` trying to reach the panel).
 *
 * @author DES
 * @date 26-05-2026
 */

import { ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/shared/auth/auth-context";
import { homePathForUser } from "@/shared/auth/homePath";

export function ForbiddenPage() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const goToSignIn = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-md text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <p className="mt-4 text-sm font-medium uppercase tracking-widest text-destructive">
          403
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">
          Access denied
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          You don&apos;t have access to this area.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          {/* Back to the signed-in person's own Home, by role: a company admin
              was sent to /app/meetings, a user route (30-09-2026). */}
          {user ? (
            <Button type="button" variant="outline" onClick={() => navigate(homePathForUser(user))}>
              Go to Home
            </Button>
          ) : null}
          <Button type="button" onClick={() => void goToSignIn()}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
